import { useState } from 'react'
import axios from '@fs/zion-axios'
import { useUser } from '@fs/zion-user'
import { clearLastViewedPid, clearStartingPidCache } from '@fs/zion-tree-groups'
import { getPersonObj } from '@fs/zion-person-service'
import useFetchData from './useFetchData'
import { TreeStartingPersonIdInvalidError } from '../errors'

const clearCaches = async () =>
  Promise.all([clearStartingPidCache(), clearLastViewedPid('person'), clearLastViewedPid('pedigree')])

const fetchTreeStartingPersonId = async (cisId) => {
  if (!cisId) {
    return null
  }
  const { data } = await axios.get(`/service/cmn/user-preferences/users/${cisId}/preferences/tree.startingPersonId`)
  return data?.value // will be null if person is the current user
}

const deleteTreeStartingPerson = async (cisId) => {
  await axios.delete(`/service/cmn/user-preferences/users/${cisId}/preferences/tree.startingPersonId`)
  await clearCaches()
}

const updateTreeStartingPerson = async (cisId, personId) => {
  await axios.put(`/service/cmn/user-preferences/users/${cisId}/preferences/tree.startingPersonId`, { value: personId })
  await clearCaches()
}

const fetchTreeStartingPerson = async (personId, { throwValidationError } = {}) => {
  if (!personId) {
    return null
  }
  try {
    const [serverPromise] = await getPersonObj(personId, 'summary', false)

    const data = await serverPromise
    return data ?? {}
  } catch (error) {
    if ((error.response?.status === 400 || error.response?.status === 404) && throwValidationError) {
      // this code path should be rare with proper client validation
      throw new TreeStartingPersonIdInvalidError()
    } else {
      throw error
    }
  }
}

function useTreeStartingPersonState({ cisId, personId }) {
  // use localTreeStartingPerson false if using the default starting person (the user)
  // set it to null initially so state updates fire if setting it later
  const [localTreeStartingPersonId, setLocalTreeStartingPersonId] = useState(null)
  const [localTreeStartingPerson, setLocalTreeStartingPerson] = useState(null)

  const handleChangeStartingPerson = async (newPersonId) => {
    setLocalTreeStartingPerson(false)
    const isDefaultPerson = newPersonId === personId
    try {
      if (isDefaultPerson) {
        setLocalTreeStartingPersonId(false)
        await deleteTreeStartingPerson(cisId)
      } else {
        setLocalTreeStartingPersonId(newPersonId)
        // fetch the name first before updating so that we don't set an invalid person ID
        // which would result in breaking the user's default tree view
        const newName = await fetchTreeStartingPerson(newPersonId, { throwValidationError: true })
        setLocalTreeStartingPerson(newName)
        await updateTreeStartingPerson(cisId, newPersonId)
      }
    } catch (error) {
      setLocalTreeStartingPersonId(localTreeStartingPersonId)
      setLocalTreeStartingPerson(localTreeStartingPerson)
      throw error
    }
  }
  return {
    localTreeStartingPersonId,
    localTreeStartingPerson,
    handleChangeStartingPerson,
  }
}

export default function useTreeStartingPerson() {
  const { cisId, personId, userLoading } = useUser()

  const { localTreeStartingPersonId, localTreeStartingPerson, handleChangeStartingPerson } = useTreeStartingPersonState(
    { cisId, personId }
  )

  const { data: remoteTreeStartingPersonId, loading: treeStartingPersonIdLoading } = useFetchData(
    fetchTreeStartingPersonId,
    {
      requestFnArg: cisId,
    }
  )
  const { data: remoteTreeStartingPerson, loading: treeStartingPersonLoading } = useFetchData(fetchTreeStartingPerson, {
    requestFnArg: remoteTreeStartingPersonId,
  })
  const treeStartingPersonId = localTreeStartingPersonId ?? remoteTreeStartingPersonId
  const treeStartingPerson = localTreeStartingPerson ?? remoteTreeStartingPerson

  const { data: defaultPerson, loading: defaultPersonLoading } = useFetchData(fetchTreeStartingPerson, {
    requestFnArg: personId,
  })

  return {
    // the user
    defaultStartingPerson: {
      personId,
      ...defaultPerson,
    },
    useDefaultStartingPerson: !treeStartingPersonId,
    treeStartingPerson: {
      personId: treeStartingPersonId,
      ...treeStartingPerson,
    },
    treeStartingPersonLoading:
      userLoading || treeStartingPersonIdLoading || treeStartingPersonLoading || defaultPersonLoading,
    handleChangeStartingPerson,
  }
}
